import React, { useState, useEffect, useRef } from 'react';
import { useTranslation } from 'react-i18next';
import { applyVideoFilter, checkTaskStatus } from '../../services/videoApi';
import { 
    Upload, Download, Loader2, AlertCircle, 
    CheckCircle, XCircle, FileUp, Wand2
} from 'lucide-react';
import { useAuth } from '../../contexts/AuthContext';
import { Link } from 'react-router-dom';

const SubscribePrompt = () => {
    const { t } = useTranslation();
    const PLAN_PRICE = "14,90 €"; 

    return (
        <div className="flex items-center justify-center h-full p-4">
            <div className="max-w-md w-full bg-brand-dark border-2 border-brand-orange rounded-lg p-8 shadow-lg">
                <h2 className="text-center text-3xl font-bold text-brand-orange mb-4">
                    {PLAN_PRICE}/{t('subscribePrompt.perMonth')}
                </h2>
                <ul className="text-slate-200 space-y-3 list-disc list-inside mb-8">
                    <li>{t('subscribePrompt.feature1')}</li>
                    <li>{t('subscribePrompt.feature2')}</li>
                    <li>{t('subscribePrompt.feature3')}</li>
                    <li>{t('subscribePrompt.feature4')}</li>
                    <li>{t('subscribePrompt.feature5')}</li>
                </ul>
                <Link 
                    to="/pricing"
                    className="w-full block text-center px-6 py-3 bg-brand-orange text-white font-semibold rounded-md hover:bg-orange-600 transition-colors"
                >
                    {t('subscribePrompt.subscribeButton')}
                </Link>
            </div>
        </div>
    );
};

const FILTERS = ["Grayscale", "Blur", "Edge Detection", "Sepia", "Invert", "Sharpen", "Emboss", "Cartoon"];

const ApplyFilter = () => {
    const { t } = useTranslation();
    const { user, setCredits } = useAuth();
    
    // UI State
    const [file, setFile] = useState(null);
    const [fileName, setFileName] = useState('');
    const [previewUrl, setPreviewUrl] = useState(null);
    const [filterType, setFilterType] = useState('Grayscale');
    const [kernelSize, setKernelSize] = useState(15);
    const [error, setError] = useState(null);
    
    // Process State
    const [isProcessing, setIsProcessing] = useState(false);
    const [taskId, setTaskId] = useState(null);
    const [progressValue, setProgressValue] = useState(0);
    const [resultUrl, setResultUrl] = useState(null);
    
    const pollingIntervalRef = useRef(null);

    useEffect(() => {
        return () => {
            if (previewUrl) URL.revokeObjectURL(previewUrl);
            if (pollingIntervalRef.current) clearInterval(pollingIntervalRef.current);
        };
    }, [previewUrl]);

    // Polling Logic
    useEffect(() => {
        if (taskId && isProcessing) {
            pollingIntervalRef.current = setInterval(async () => {
                try {
                    const data = await checkTaskStatus(taskId);
                    if (data.state === 'SUCCESS' || data.status === 'SUCCESS') {
                        clearInterval(pollingIntervalRef.current);
                        const payload = data.result;
                        if (payload.new_credits !== undefined) setCredits(payload.new_credits);
                        if (payload.result && payload.result.download_url) {
                            setResultUrl(payload.result.download_url);
                            setProgressValue(100);
                            setIsProcessing(false);
                        }
                    } else if (data.state === 'FAILURE') {
                        clearInterval(pollingIntervalRef.current);
                        setError(data.error);
                        setIsProcessing(false);
                    } else if (data.info && data.info.progress) {
                        setProgressValue(data.info.progress);
                    }
                } catch (err) {
                    clearInterval(pollingIntervalRef.current);
                    setError(err.message);
                    setIsProcessing(false);
                }
            }, 2000);
        }
        return () => {
            if (pollingIntervalRef.current) clearInterval(pollingIntervalRef.current);
        };
    }, [taskId, isProcessing, setCredits]);

    const handleFileChange = (e) => {
        const selected = e.target.files[0];
        if (selected) {
            if (selected.size > 50 * 1024 * 1024) {
                setError(t('applyFilter.errors.tooLarge'));
                return;
            }
            setFile(selected);
            setFileName(selected.name);
            if (previewUrl) URL.revokeObjectURL(previewUrl);
            setPreviewUrl(URL.createObjectURL(selected));
            setError(null);
            setResultUrl(null);
            setProgressValue(0);
        }
    };

    const handleProcess = async () => {
        if (!file) return;
        setIsProcessing(true);
        setProgressValue(0);
        setError(null);
        
        try {
            const response = await applyVideoFilter(file, filterType, kernelSize);
            setTaskId(response.task_id);
        } catch (err) {
            setError(err.message);
            setIsProcessing(false);
        }
    };

    const UploadView = () => (
        <div className="max-w-4xl mx-auto">
            <h2 className="text-2xl font-semibold mb-2 text-brand-orange">{t('applyFilter.title')}</h2>
            <p className="mt-2 text-brand-dark">{t('applyFilter.description')}</p>
            
            <div className="mt-6 space-y-6">
                {/* File Upload */}
                <div>
                    <label className="block text-sm font-medium mb-2 text-brand-dark">{t('applyFilter.uploadLabel')}</label>
                    <div className="flex items-center p-4 border-2 border-dashed border-slate-400 rounded-md bg-slate-50">
                        <FileUp className="h-8 w-8 text-slate-500" />
                        <label htmlFor="filter-upload" className="ml-4 text-sm text-brand-orange font-semibold cursor-pointer hover:text-orange-600">
                            {t('applyFilter.selectFile')}
                            <input id="filter-upload" type="file" className="sr-only" onChange={handleFileChange} accept=".mp4,.avi,.mov" />
                        </label>
                        {fileName && (
                            <div className="ml-auto text-sm text-slate-700">{fileName}</div>
                        )}
                    </div>
                </div>

                {/* Preview */}
                {previewUrl && (
                    <div className="rounded-lg overflow-hidden bg-black aspect-video flex items-center justify-center">
                        <video src={previewUrl} controls className="w-full h-full object-contain" />
                    </div>
                )}

                {/* Settings */}
                <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                    <div>
                        <label className="block text-sm font-medium mb-2 text-brand-dark">{t('applyFilter.filterType')}</label>
                        <select 
                            value={filterType}
                            onChange={(e) => setFilterType(e.target.value)}
                            className="w-full p-2 border border-slate-300 rounded-md"
                        >
                            {FILTERS.map(f => (
                                <option key={f} value={f}>{f}</option>
                            ))}
                        </select>
                    </div>

                    {filterType === 'Blur' && (
                        <div>
                            <label className="block text-sm font-medium mb-2 text-brand-dark">{t('applyFilter.intensity')}: {kernelSize}</label>
                            <input 
                                type="range" min="3" max="50" step="2"
                                value={kernelSize}
                                onChange={(e) => setKernelSize(parseInt(e.target.value))}
                                className="w-full h-2 bg-slate-200 rounded-lg appearance-none cursor-pointer accent-brand-orange"
                            />
                        </div>
                    )}
                </div>

                {error && <p className="text-red-500 flex items-center"><AlertCircle size={16} className="mr-2"/>{error}</p>}

                <button
                    onClick={handleProcess}
                    disabled={!file || isProcessing}
                    className="w-full md:w-auto px-8 py-3 bg-brand-orange text-white font-semibold rounded-md hover:bg-orange-600 transition-colors disabled:bg-slate-400 disabled:cursor-not-allowed flex items-center justify-center"
                >
                    {isProcessing ? (
                        <><Loader2 className="animate-spin mr-2" size={20} /> {t('applyFilter.processing')}</>
                    ) : (
                        t('applyFilter.processButton')
                    )}
                </button>

                {isProcessing && (
                     <div className="bg-slate-50 p-4 rounded-md border border-slate-200">
                        <div className="flex justify-between text-sm font-medium text-slate-700 mb-2">
                            <span>{t('applyFilter.filtering')}</span>
                            <span>{progressValue}%</span>
                        </div>
                        <div className="w-full bg-slate-200 rounded-full h-2.5 overflow-hidden">
                            <div className="bg-brand-orange h-2.5 rounded-full transition-all duration-500" style={{ width: `${progressValue}%` }}></div>
                        </div>
                    </div>
                )}
            </div>
        </div>
    );

    const ResultView = () => (
        <div className="max-w-4xl mx-auto text-center">
            <div className="bg-green-50 border border-green-200 p-4 rounded-md mb-8 flex items-center justify-center text-green-800">
                <CheckCircle className="mr-2" /> {t('applyFilter.success')}
            </div>
            <div className="rounded-lg overflow-hidden bg-black aspect-video mb-6 shadow-lg">
                <video src={resultUrl} controls className="w-full h-full object-contain" />
            </div>
            <a href={resultUrl} download className="inline-flex items-center px-8 py-3 bg-brand-orange text-white font-semibold rounded-md hover:bg-orange-600">
                <Download className="mr-2" size={20} /> {t('applyFilter.download')}
            </a>
             <button onClick={() => { setFile(null); setFileName(''); setResultUrl(null); }} className="block w-full mt-6 text-slate-600 hover:text-brand-orange underline">
                {t('applyFilter.processAnother')}
            </button>
        </div>
    );

    return (
        <div className="p-6 md:p-8 h-full overflow-y-auto text-brand-dark bg-brand-white">
            {(user?.subscription_status !== 'active' && (!user?.is_verified || user?.credits <= 2)) ? <SubscribePrompt />  : resultUrl ? <ResultView /> : <UploadView />}
        </div>
    );
};

export default ApplyFilter;


